/**
 * forecastView.ts — the Forecast tab's render rules, pure (2.3b, §18.4).
 *
 * Every rule here exists to keep an ABSENT figure from reading as a ZERO one,
 * and to render the server's verdict rather than recompute it:
 *
 *   U-5  a null target reads "No target set", a null attainment reads "—".
 *        Not 0, not 0%, not blank — a blank cell in a money column reads as
 *        zero to anyone who has used a spreadsheet. A REAL target of £0
 *        renders "£0.00", distinguishable from both.
 *   U-4  the attribution panel states BOTH halves (value AND count) and the
 *        verdict, and the verdict is `attainmentMeasurable` as the server
 *        decided it (R1) — the threshold lives server-side precisely so two
 *        surfaces cannot disagree.
 *   U-7  every category bucket renders, zeros included, and the bar colour
 *        binds by SLUG (tokens.forecastCategoryBar), never by index.
 *   U-9  excludedNonGbp > 0 renders one quiet line naming the count — the
 *        nonGbpNote idiom every fenced figure on the platform carries.
 */
import type {
  SalesForecast, SalesForecastCategoryCell, SalesForecastOverrideCoverage, SalesForecastOverridePair,
} from '../../../../api/types'
import { forecastCategoryBar } from '../../../../theme/tokens'
import { money, nonGbpNote, signedMoney } from '../../../../lib/money'
import { forecastCategoryLabel } from './vocab'

/** What a null target reads as. Words, so a reader concludes "nobody has set
 *  a target" — different from a target of zero and from attaining nothing. */
export const NO_TARGET_LABEL = 'No target set'

/** What a null attainment reads as — the Reports KPI precedent for a figure
 *  that is not computable yet. */
export const ABSENT = '—'

export function targetCell(targetPence: number | null): string {
  return targetPence === null ? NO_TARGET_LABEL : money(targetPence / 100)
}

/** One decimal, not a whole percent: a real 0.4% must not round to a "0%"
 *  that U-5 forbids for a different reason. */
export function attainmentCell(pct: number | null): string {
  return pct === null ? ABSENT : `${pct.toFixed(1)}%`
}

export function dealCount(n: number): string {
  return `${n} ${n === 1 ? 'deal' : 'deals'}`
}

type Attribution = Pick<SalesForecast,
  'unattributedWonPence' | 'unattributedWonCount' | 'attributedWonPence' | 'attributedWonCount' | 'attainmentMeasurable'>

/** Both halves, in words. The recon's sentence, with the attributed count
 *  added because R1 asks for value AND count on both sides. */
export function attributionSentence(f: Attribution): string {
  return `${money(f.unattributedWonPence / 100)} of won value across ${dealCount(f.unattributedWonCount)} `
    + `is not attributed to anyone. ${money(f.attributedWonPence / 100)} across ${dealCount(f.attributedWonCount)} is.`
}

/** The verdict, RENDERED from the server's boolean — never derived from the
 *  two figures above, even though the arithmetic is one comparison. */
export function verdictSentence(attainmentMeasurable: boolean): string {
  return attainmentMeasurable ? 'Attainment is measurable this period.' : 'Attainment is not measurable this period.'
}

/** The verdict as a capsule word (the k-badge slot). */
export function verdictBadge(attainmentMeasurable: boolean): string {
  return attainmentMeasurable ? 'Measurable' : 'Not measurable'
}

/** The fence's exclusion note for this tab — '' when nothing was dropped,
 *  which is the case on every all-GBP tenant today. */
export function forecastNonGbpNote(excludedNonGbp: number): string {
  return nonGbpNote(excludedNonGbp, 'deal')
}

export interface RollupBar {
  category: string
  label: string
  fill: string
  pence: number
  weightedPence: number
  count: number
  /** 0–100, against the largest bucket; 0 when every bucket is empty. */
  widthPct: number
}

/** The roll-up rows in WIRE order (the server sends all five, forecast order
 *  then Uncategorised), each bound to its slug's fixed palette slot. Nothing
 *  is dropped and nothing is invented: a bucket the wire did not send is not
 *  fabricated as a zero row. An unknown slug still renders (its label
 *  verbatim, the sourceLabel rule) in the neutral Omitted slot rather than
 *  vanishing. */
export function rollupBars(rollup: SalesForecastCategoryCell[]): RollupBar[] {
  const max = Math.max(0, ...rollup.map(c => c.pence))
  return rollup.map(c => ({
    category: c.category,
    label: forecastCategoryLabel(c.category),
    fill: (forecastCategoryBar as Record<string, string>)[c.category] ?? forecastCategoryBar.omitted,
    pence: c.pence,
    weightedPence: c.weightedPence,
    count: c.count,
    widthPct: max > 0 ? (c.pence / max) * 100 : 0,
  }))
}

// ── 2.3c: the override, rendered (P2-11 "the gap is the signal") ─────────────

/** The per-deal gap chip — §17.4's sentence verbatim: "Rep: Best case →
 *  Override: Commit, −£4,000". The amount part appears ONLY when a gap can
 *  be stated: a category-only override reads the pair alone (its gap is 0
 *  by definition, and "£0.00" would suggest a money override that said
 *  nothing); an uncaptured rep value (gap null) reads the pair alone too —
 *  a figure nobody can compute is not rendered as one. repCategory '' is
 *  Uncategorised, by the vocab's rule. */
export function gapChip(repCategory: string, overrideCategory: string, gapPence: number | null, amountOverridden: boolean): string {
  const pair = `Rep: ${forecastCategoryLabel(repCategory)} → Override: ${forecastCategoryLabel(overrideCategory)}`
  if (!amountOverridden || gapPence === null) return pair
  return `${pair}, ${gapPence > 0 ? '+' : ''}${signedMoney(gapPence)}`
}

/** The rep grid's override column: "—" when none, else the count and the
 *  Σ gap ("2 overridden · −£4,000.00"). A rep whose overrides changed
 *  categories only shows the count alone — the zero Σ is not a figure. */
export function overrideColumn(count: number, gapPence: number): string {
  if (count === 0) return ABSENT
  const head = `${count} overridden`
  return gapPence !== 0 ? `${head} · ${gapPence > 0 ? '+' : ''}${signedMoney(gapPence)}` : head
}

/** The pairs behind an override column, as one tooltip/caption line:
 *  "Best case → Commit ×2 · Uncategorised → Pipeline ×1". */
export function overridePairsLine(pairs: SalesForecastOverridePair[]): string {
  return pairs.map(p => `${forecastCategoryLabel(p.repCategory)} → ${forecastCategoryLabel(p.overrideCategory)} ×${p.count}`).join(' · ')
}

/** The coverage line beside the roll-up. Three states, each in words:
 *   - no override: '' (nothing to say — the effective view IS the rep view)
 *   - some: "2 of 5 open deals carry an override (£37,000.00 of £49,500.00)."
 *   - ALL (the Fourth Coffee shape, 1 of 1): the sentence AND the statement
 *     that the effective view is the override view — the server's
 *     allOverridden, RENDERED not recomputed (the R1 rule). */
export const ALL_OVERRIDDEN_SENTENCE = 'Every open deal carries an override, so the effective view above is the override view — the rep view is shown beside it.'

export function overrideCoverageSentence(c: SalesForecastOverrideCoverage): string {
  if (c.overriddenCount === 0) return ''
  const base = `${c.overriddenCount} of ${c.openCount} open ${c.openCount === 1 ? 'deal carries' : 'deals carry'} an override`
    + ` (${money(c.overriddenPence / 100)} of ${money(c.openPence / 100)}).`
  return c.allOverridden ? `${base} ${ALL_OVERRIDDEN_SENTENCE}` : base
}
